<script setup>
import { computed, ref, watch } from 'vue';

// eslint-disable-next-line no-undef
const props = defineProps({
    label: {
        type: String,
        required: false,
    },
    required: {
        type: Boolean,
        required: false,
    },
    modelValue: {
        type: String,
        default: '',
    },
    options: {
        type: [Array, Object],
        default: () => [],
    },
    optionLabel: {
        type: String,
        default: 'name',
    },
    optionValue: {
        type: String,
        default: 'id',
    },
    placeholder: {
        type: String,
        default: 'Select',
    },
    labelClass: {
        type: String,
        default: '',
    },
    // Just use the predefined value in TailwindCSS: https://tailwindcss.com/docs/width, but only passed the value as
    // it's already being prefixed here.
    dropdownWidth: {
        type: String,
        default: 'full',
    },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue']);

const selected = ref(props.modelValue);
watch(selected, (newVal) => {
    emit('update:modelValue', newVal);
});

const computedDropdownWith = computed(() => `tw-w-${props.dropdownWidth}`);

/** Styling */
const passThrough = {
    root: () => ({
        class: [
            'tw-h-[51px] tw-border-0 tw-bg-[#312C700D] tw-pl-8 tw-pr-5 tw-flex tw-items-center',
            computedDropdownWith.value,
        ],
    }),
    input: 'tw-text-[20px] !tw-text-secondary',
    wrapper: 'tw-bg-[#F9F9FB]',
    item: ({ context }) => ({
        class: [
            'tw-h-[51px] tw-flex tw-items-center tw-text-[20px] !tw-text-secondary !tw-pl-8 !tw-pr-5',
            context.selected ? '!tw-bg-[#F1F0F5]' : '',
        ],
    }),
};
</script>

<template>
    <div class="tw-flex tw-flex-col tw-gap-8 tw-justify-end">
        <label
            v-if="props.label"
            for=""
            class="!tw-text-[1.2em] tw-font-semibold"
            :class="props.labelClass"
        >
            {{ props.label }}
            <span v-if="props.required" class="tw-text-red-700">*</span>
        </label>

        <Dropdown
            v-model="selected"
            append-to="self"
            :options="props.options"
            :optionLabel="props.optionLabel"
            :optionValue="props.optionValue"
            :placeholder="props.placeholder"
            :pt="passThrough"
        />
    </div>
</template>

<style scoped>
:deep(.p-dropdown-item:not(.p-highlight):not(.p-disabled):hover) {
    @apply !tw-bg-[#F1F0F5];
}
</style>
